// The deliverable drawers open from the URL, so any row on any program page
// can link to one and a reload keeps it open:
//
//   ?occ=<occurrenceId>              the date (wireframe 1)
//   ?submit=<occurrenceId|new>       the submit drawer (wireframe 2); "new" = pick the date
//   ?review=<submissionId>           Record review, over ?occ
//   ?assign=<teamId>:<sowId>:<dutyId|new>  a SOW's version of a deliverable (D105)
//
// The drawers store only these ids and derive their records from queries
// keyed on them (never an effect that copies a row into state).
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
export function useCmDrawerParams() {
    const [params, setParams] = useSearchParams();
    const get = (k)=>params.get(k) ?? '';
    const open = useCallback((kind, value, extra)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            next.set(kind, value);
            for (const [k, v] of Object.entries(extra ?? {})){
                if (v) next.set(k, v);
                else next.delete(k);
            }
            return next;
        }, {
            replace: false
        });
    }, [
        setParams
    ]);
    const close = useCallback((...kinds)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const k of kinds)next.delete(k);
            return next;
        }, {
            replace: false
        });
    }, [
        setParams
    ]);
    /**
   * Clear some params and set others in ONE navigation. Two back-to-back
   * setParams calls would each start from the same stale URL, so the second
   * would silently undo the first (the submit drawer staying open after it
   * hands over to the date).
   */ const swap = useCallback((clear, set)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            for (const k of clear)next.delete(k);
            for (const [k, v] of Object.entries(set))if (v) next.set(k, v);
            return next;
        }, {
            replace: false
        });
    }, [
        setParams
    ]);
    return {
        swap,
        occ: get('occ'),
        submit: get('submit'),
        review: get('review'),
        assign: get('assign'),
        /** Pre-picks the deliverable in a "new" submit drawer. */ forDuty: params.get('forDuty') ?? '',
        /** Narrows a "new" submit drawer's deliverables to one team. */ forTeam: params.get('forTeam') ?? '',
        open,
        close
    };
}
